import { useCallback, useEffect, useRef, useState } from "react";
import { createApi } from "../../../utils/createApi";
import { SiteCamerasResult } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";

export type SiteWallCamerasStatus = "idle" | "loading" | "ready" | "error";

interface CameraSitePageResponse {
  count: number;
  next: string | number | null;
  previous: string | number | null;
  results: SiteCamerasResult[];
}

export const useSiteWallCameras = (
  siteId: number | null,
  enabled: boolean
) => {
  const [status, setStatus] = useState<SiteWallCamerasStatus>("idle");
  const [cameras, setCameras] = useState<SiteCamerasResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const nextPageRef = useRef<number | null>(null);
  const fetchIdRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const camerasLengthRef = useRef(0);

  const reset = useCallback(() => {
    setStatus("idle");
    setCameras([]);
    setTotalCount(0);
    nextPageRef.current = null;
    loadingMoreRef.current = false;
    camerasLengthRef.current = 0;
  }, []);

  const fetchPage = useCallback(
    async (pageNumber: number, fetchId: number): Promise<SiteCamerasResult[]> => {
      if (!siteId) return [];

      const AxiosUI = createApi("");
      const response = await AxiosUI.get<CameraSitePageResponse>(
        `camera/site/?customer__id=${siteId}&page=${pageNumber}`
      );

      if (fetchId !== fetchIdRef.current) return [];

      const { count, next, results } = response.data;
      setTotalCount(count);
      nextPageRef.current = next ? pageNumber + 1 : null;

      setCameras((prev) => {
        const merged = pageNumber === 1 ? results : [...prev, ...results];
        camerasLengthRef.current = merged.length;
        return merged;
      });
      setStatus("ready");
      return results;
    },
    [siteId]
  );

  useEffect(() => {
    if (!enabled || !siteId) {
      fetchIdRef.current += 1;
      reset();
      return;
    }

    const fetchId = ++fetchIdRef.current;
    setStatus("loading");
    setCameras([]);
    setTotalCount(0);
    nextPageRef.current = null;
    camerasLengthRef.current = 0;

    fetchPage(1, fetchId).catch(() => {
      if (fetchId !== fetchIdRef.current) return;
      setStatus("error");
    });
  }, [enabled, siteId, fetchPage, reset]);

  const ensureCamerasLoaded = useCallback(
    async (upToIndex: number) => {
      if (!siteId || status === "idle" || status === "loading") return;
      if (camerasLengthRef.current > upToIndex) return;
      if (!nextPageRef.current || loadingMoreRef.current) return;

      const fetchId = fetchIdRef.current;
      loadingMoreRef.current = true;

      try {
        while (
          nextPageRef.current &&
          camerasLengthRef.current <= upToIndex &&
          fetchId === fetchIdRef.current
        ) {
          await fetchPage(nextPageRef.current, fetchId);
        }
      } catch {
        if (fetchId === fetchIdRef.current) {
          setStatus("error");
        }
      } finally {
        loadingMoreRef.current = false;
      }
    },
    [siteId, status, fetchPage]
  );

  return {
    status,
    cameras,
    totalCount,
    ensureCamerasLoaded,
    reset,
  };
};
